
import { A2AAgent } from '../../types';
import { getOrchestrationMetrics } from '../../learning/adaptiveOrchestration';

export function determineOptimalPattern(
  requiredCapabilities: string[],
  taskComplexity: 'simple' | 'medium' | 'complex' = 'medium'
): 'sequential' | 'parallel' | 'hierarchical' {
  const defaultPattern: Record<string, 'sequential' | 'parallel' | 'hierarchical'> = {
    simple: 'sequential',
    medium: requiredCapabilities.length <= 2 ? 'sequential' : 'parallel',
    complex: 'hierarchical'
  };
  
  const relevantPatterns = getOrchestrationMetrics()
    .filter(metrics => metrics.patternType === defaultPattern[taskComplexity] && metrics.sampleSize >= 3);
  
  if (relevantPatterns.length === 0) {
    return defaultPattern[taskComplexity];
  }
  
  relevantPatterns.sort((a, b) => {
    if (Math.abs(a.successRate - b.successRate) > 0.1) {
      return b.successRate - a.successRate;
    }
    return a.averageCompletionTime - b.averageCompletionTime;
  });
  
  return relevantPatterns[0].patternType;
}

export function buildSequentialAgentSequence(
  availableAgents: A2AAgent[],
  requiredCapabilities: string[]
): string[] {
  const remainingCapabilities = [...requiredCapabilities];
  const sequence: string[] = [];
  const usedAgentIds = new Set<string>();
  
  while (remainingCapabilities.length > 0 && availableAgents.length > usedAgentIds.size) {
    const agentScores = availableAgents
      .filter(agent => !usedAgentIds.has(agent.id))
      .map(agent => {
        const coverageCount = agent.capabilities.filter(
          cap => remainingCapabilities.includes(cap)
        ).length;
        
        return {
          agent,
          coverageCount
        };
      });
    
    agentScores.sort((a, b) => b.coverageCount - a.coverageCount);
    
    if (agentScores.length === 0 || agentScores[0].coverageCount === 0) {
      break;
    }
    
    const bestAgent = agentScores[0].agent;
    sequence.push(bestAgent.id);
    usedAgentIds.add(bestAgent.id);
    
    const coveredCapabilities = bestAgent.capabilities.filter(
      cap => remainingCapabilities.includes(cap)
    );
    
    coveredCapabilities.forEach(cap => {
      const idx = remainingCapabilities.indexOf(cap);
      if (idx !== -1) remainingCapabilities.splice(idx, 1);
    });
  }
  
  return sequence;
}

export function buildParallelAgentSequence(
  availableAgents: A2AAgent[],
  requiredCapabilities: string[]
): string[] {
  const agentSequence: string[] = [];
  const usedAgentIds = new Set<string>();
  
  requiredCapabilities.forEach(capability => {
    const candidateAgents = availableAgents.filter(
      agent => !usedAgentIds.has(agent.id) && agent.capabilities.includes(capability)
    );
    
    if (candidateAgents.length === 0) {
      const existingAgentHasCapability = agentSequence.some(agentId => {
        const agent = availableAgents.find(a => a.id === agentId);
        return agent && agent.capabilities.includes(capability);
      });
      
      if (!existingAgentHasCapability) {
        const specializedAgents = availableAgents.filter(
          agent => agent.capabilities.includes(capability)
        );
        
        if (specializedAgents.length > 0) {
          specializedAgents.sort((a, b) => a.capabilities.length - b.capabilities.length);
          agentSequence.push(specializedAgents[0].id);
        }
      }
    } else {
      candidateAgents.sort((a, b) => a.capabilities.length - b.capabilities.length);
      agentSequence.push(candidateAgents[0].id);
      usedAgentIds.add(candidateAgents[0].id);
    }
  });
  
  return [...new Set(agentSequence)];
}

export function buildHierarchicalAgentSequence(
  availableAgents: A2AAgent[],
  requiredCapabilities: string[]
): string[] {
  const coordinatorCandidates = availableAgents.filter(agent => {
    const requiredCapabilitiesCount = agent.capabilities.filter(
      cap => requiredCapabilities.includes(cap)
    ).length;
    return requiredCapabilitiesCount >= 2;
  });
  
  coordinatorCandidates.sort((a, b) => {
    const aCoverage = a.capabilities.filter(cap => requiredCapabilities.includes(cap)).length;
    const bCoverage = b.capabilities.filter(cap => requiredCapabilities.includes(cap)).length;
    return bCoverage - aCoverage;
  });
  
  const coordinators = coordinatorCandidates.slice(0, Math.min(2, coordinatorCandidates.length));
  const coordinatorIds = coordinators.map(c => c.id);
  
  const coveredCapabilities = new Set<string>();
  coordinators.forEach(agent => {
    agent.capabilities.forEach(cap => {
      if (requiredCapabilities.includes(cap)) {
        coveredCapabilities.add(cap);
      }
    });
  });
  
  const uncoveredCapabilities = requiredCapabilities.filter(
    cap => !coveredCapabilities.has(cap)
  );
  
  const specialistIds: string[] = [];
  
  uncoveredCapabilities.forEach(capability => {
    const specialists = availableAgents.filter(
      agent => !coordinatorIds.includes(agent.id) && agent.capabilities.includes(capability)
    );
    
    if (specialists.length > 0) {
      specialists.sort((a, b) => a.capabilities.length - b.capabilities.length);
      specialistIds.push(specialists[0].id);
    }
  });
  
  return [...coordinatorIds, ...specialistIds];
}

export function estimateCompletionTime(
  pattern: 'sequential' | 'parallel' | 'hierarchical',
  agentCount: number
): number {
  const baseTime = 1000;
  
  switch (pattern) {
    case 'sequential':
      return baseTime * agentCount;
    case 'parallel':
      return baseTime + (baseTime * 0.5);
    case 'hierarchical':
      return baseTime * agentCount * 0.6;
  }
}
